import type {
  FlussonicHlsVideoPlayerSavedPlaybackState,
  FlussonicHlsVideoPlayerSettings,
  FlussonicHlsVideoPlayerViewSettings,
} from '@repo/hls-flussonic-video-player';
import { makeAutoObservable } from 'mobx';

import type {
  VideoBroadcastEvents,
  VideoBroadcastInterval,
  VideoBroadcastPlaybackMode,
  VideoBroadcastPlaybackTimeSource,
  VideoBroadcastStateParams,
} from '../types';

import {
  getCenteredTimelineViewportAfterExternalSeek,
  hasConfirmedPendingSeekPlayback,
  isPlayerProgressTimeAccepted,
} from './videoBroadcastPlaybackSyncHelpers';
import type { VideoBroadcastStatePublisher } from './VideoBroadcastStatePublisher';

const LOCAL_PLAYBACK_STATE_SAVE_INTERVAL_MS = 2000;

type PendingPlayerSeek = {
  missingArchiveCameraKeys: string[];
  previousTimeMs: number | null;
  timeMs: number;
};

type PendingLocalPlaybackStateSave = {
  playbackMode: VideoBroadcastPlaybackMode;
  playbackTimeMs: number;
};

type PlayerSettingsUpdateOptions = {
  immediate?: boolean;
};

export type VideoPlaybackSyncCamera = {
  id: number;
  liveAvailable: boolean | null;
  liveEdgeMs: number | null;
  setLiveStatus(status: { liveAvailable: boolean | null; liveEdgeMs: number | null }): void;
};

type VideoPlaybackSyncStoreContext<TCamera extends VideoPlaybackSyncCamera> = {
  eventHandlers: VideoBroadcastEvents;
  getCameraSettingsKey: (camera: TCamera) => string;
  getPlayerSettingsByCameraId: () => Record<string, FlussonicHlsVideoPlayerSettings>;
  getSelectedCameras: () => TCamera[];
  getVisibleCameraKeys: () => string[];
  setPlayerSettingsByCameraId: (settings: Record<string, FlussonicHlsVideoPlayerSettings>) => void;
  statePublisher: Pick<VideoBroadcastStatePublisher, 'debouncedSetParams' | 'setParams'>;
};

export class VideoPlaybackSyncStore<TCamera extends VideoPlaybackSyncCamera> {
  playbackMode: VideoBroadcastPlaybackMode = 'live';

  playbackTimeMs: number | null = null;

  videoInterval: VideoBroadcastInterval | null = null;

  // Increment only for deliberate commands that must recreate the player
  // instance and make it apply the current playbackMode/playbackTimeMs.
  playerRemountKey = 0;

  linkedVideoSyncEnabled = false;

  private pendingPlayerSeek: PendingPlayerSeek | null = null;

  private localPlaybackStateSaveTimers = new Map<string, ReturnType<typeof setTimeout>>();

  private pendingLocalPlaybackStateSaves = new Map<string, PendingLocalPlaybackStateSave>();

  constructor(private readonly context: VideoPlaybackSyncStoreContext<TCamera>) {
    makeAutoObservable<this, 'context' | 'localPlaybackStateSaveTimers' | 'pendingLocalPlaybackStateSaves'>(this, {
      context: false,
      localPlaybackStateSaveTimers: false,
      pendingLocalPlaybackStateSaves: false,
    });
  }

  seekToTime(timeMs: number) {
    if (!this.linkedVideoSyncEnabled) {
      return;
    }

    this.applyExternalArchiveSeek(this.clampToVideoInterval(timeMs));
  }

  setExternalPlaybackTime(timeMs: number): void {
    if (!this.linkedVideoSyncEnabled) {
      return;
    }

    this.applyExternalArchiveSeek(this.clampToVideoInterval(timeMs));
  }

  showVideoInterval({ fromMs, toMs }: { fromMs: number; toMs: number }) {
    if (!this.linkedVideoSyncEnabled) {
      return;
    }

    const startMs = Math.min(fromMs, toMs);
    const endMs = Math.max(fromMs, toMs);

    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs) {
      return;
    }

    this.videoInterval = {
      fromMs: startMs,
      toMs: endMs,
    };
    this.context.eventHandlers.onVideoIntervalChanged?.(this.videoInterval);
    this.applyExternalArchiveSeek(startMs);
  }

  clearVideoInterval() {
    const hadVideoInterval = this.videoInterval != null;

    this.videoInterval = null;

    if (hadVideoInterval) {
      this.context.eventHandlers.onVideoIntervalChanged?.(null);
    }
  }

  setLinkedVideoSyncEnabled(enabled: boolean, timeMs: number | null = null) {
    if (this.linkedVideoSyncEnabled === enabled) {
      return;
    }

    this.linkedVideoSyncEnabled = enabled;

    if (!enabled) {
      this.clearVideoInterval();
      this.clearPendingPlayerSeek();
      return;
    }

    if (timeMs != null) {
      this.applyExternalArchiveSeek(timeMs);
    }
  }

  seekRelativeToCurrentTime(deltaMs: number) {
    const currentPlaybackTimeMs = this.currentPlaybackTimeMs;
    if (currentPlaybackTimeMs == null) {
      return;
    }

    this.seekToTime(currentPlaybackTimeMs + deltaMs);
  }

  setLiveMode() {
    if (!this.linkedVideoSyncEnabled || this.liveModeBlocked || this.videoInterval != null) {
      return;
    }

    const liveTimeMs = this.latestSelectedLiveEdgeMs ?? Date.now();

    this.playbackMode = 'live';
    this.playbackTimeMs = liveTimeMs;
    this.clearPendingPlayerSeek();
    this.playerRemountKey += 1;

    this.context.eventHandlers.onPlaybackTimeChanged?.(liveTimeMs, 'player');
  }

  get currentPlaybackTimeMs(): number | null {
    return this.playbackTimeMs;
  }

  setPlayerPlaybackTime(
    camera: TCamera,
    timeMs: number,
    source: VideoBroadcastPlaybackTimeSource,
    playbackMode: VideoBroadcastPlaybackMode,
  ) {
    if (!this.linkedVideoSyncEnabled) {
      this.savePlayerPlaybackState(camera, {
        immediate: source === 'timeline',
        playbackMode,
        playbackTimeMs: timeMs,
      });
      this.context.eventHandlers.onPlaybackTimeChanged?.(timeMs, source);
      return;
    }

    const isTimelineSeek = source === 'timeline';
    const boundedTimeMs = this.clampToVideoInterval(timeMs);

    if (isTimelineSeek) {
      this.playbackMode = 'archive';
      this.startPendingPlayerSeek(boundedTimeMs);
      this.playbackTimeMs = boundedTimeMs;
      this.playerRemountKey += 1;

      this.context.eventHandlers.onPlaybackTimeChanged?.(boundedTimeMs, source);
      return;
    }

    const pendingPlayerSeekBeforeReport = this.pendingPlayerSeek;
    const acceptedAfterPendingSeek = pendingPlayerSeekBeforeReport != null;
    if (this.isVideoIntervalEndReport(timeMs, boundedTimeMs)) {
      this.clearPendingPlayerSeek();
      this.playbackTimeMs = boundedTimeMs;
      this.context.eventHandlers.onPlaybackTimeChanged?.(boundedTimeMs, source);
      return;
    }

    if (!this.shouldAcceptPlayerTimeAfterSeek(boundedTimeMs)) {
      return;
    }

    if (!acceptedAfterPendingSeek && !this.shouldAcceptPlayerProgressTime(boundedTimeMs)) {
      return;
    }

    this.playbackTimeMs = boundedTimeMs;
    this.context.eventHandlers.onPlaybackTimeChanged?.(boundedTimeMs, source);
  }

  getPlayerSettings(camera: TCamera) {
    const settings = this.playerSettingsByCameraId[this.context.getCameraSettingsKey(camera)];

    if (this.videoInterval == null) {
      return settings;
    }

    return {
      ...settings,
      timelineViewport: {
        endMs: this.videoInterval.toMs,
        mode: 'fixed' as const,
        startMs: this.videoInterval.fromMs,
      },
    };
  }

  getPlayerInitialPlaybackState(camera: TCamera) {
    const savedPlaybackState = getLocalSavedPlaybackState(this.getPlayerSettings(camera));

    if (
      savedPlaybackState != null &&
      savedPlaybackState.playbackMode !== 'live' &&
      savedPlaybackState.playbackTimeMs != null
    ) {
      return {
        mode: 'archive' as const,
        timeMs: savedPlaybackState.playbackTimeMs,
      };
    }

    if (savedPlaybackState?.playbackMode === 'live') {
      return {
        mode: 'live' as const,
        timeMs: savedPlaybackState.playbackTimeMs ?? null,
      };
    }

    if (this.linkedVideoSyncEnabled && this.playbackMode === 'archive' && this.playbackTimeMs != null) {
      return {
        mode: 'archive' as const,
        timeMs: this.playbackTimeMs,
      };
    }

    return {
      mode: 'live' as const,
      timeMs: null,
    };
  }

  setPlayerSettings(
    camera: TCamera,
    settings: Partial<FlussonicHlsVideoPlayerViewSettings>,
    options: PlayerSettingsUpdateOptions = {},
  ) {
    const cameraKey = this.context.getCameraSettingsKey(camera);

    if (this.linkedVideoSyncEnabled && settings.playbackRate != null) {
      this.setSyncedPlayerPlaybackRate(cameraKey, settings, options);
      return;
    }

    this.setPlayerViewSettingsByCameraKey(cameraKey, settings, options);
  }

  setCameraLiveStatus(
    camera: TCamera,
    status: {
      liveAvailable: boolean | null;
      liveEdgeMs: number | null;
    },
  ) {
    const wasLiveBlocked = this.liveModeBlocked;

    camera.setLiveStatus(status);

    if (!this.linkedVideoSyncEnabled || this.playbackMode !== 'live' || wasLiveBlocked || !this.liveModeBlocked) {
      return;
    }

    const fallbackTimeMs = this.playbackTimeMs ?? this.latestSelectedLiveEdgeMs ?? Date.now();

    this.playbackMode = 'archive';
    this.startPendingPlayerSeek(fallbackTimeMs);
    this.playbackTimeMs = fallbackTimeMs;
    this.playerRemountKey += 1;

    this.context.eventHandlers.onPlaybackTimeChanged?.(fallbackTimeMs, 'player');
  }

  confirmPlayerArchiveUnavailable(camera: TCamera, timeMs: number) {
    if (!this.linkedVideoSyncEnabled || this.pendingPlayerSeek == null) {
      return;
    }

    if (Math.abs(this.pendingPlayerSeek.timeMs - timeMs) > 1000) {
      return;
    }

    const cameraKey = this.context.getCameraSettingsKey(camera);

    if (!this.pendingPlayerSeek.missingArchiveCameraKeys.includes(cameraKey)) {
      this.pendingPlayerSeek.missingArchiveCameraKeys.push(cameraKey);
    }

    const activeCameraKeys = this.context.getVisibleCameraKeys();
    const cameraKeysToConfirm = activeCameraKeys.length > 0 ? activeCameraKeys : [cameraKey];

    if (cameraKeysToConfirm.every((key) => this.pendingPlayerSeek?.missingArchiveCameraKeys.includes(key))) {
      this.clearPendingPlayerSeek();
    }
  }

  destroy() {
    this.localPlaybackStateSaveTimers.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.localPlaybackStateSaveTimers.clear();
    this.pendingLocalPlaybackStateSaves.clear();
  }

  private get selectedCameras() {
    return this.context.getSelectedCameras();
  }

  private get playerSettingsByCameraId() {
    return this.context.getPlayerSettingsByCameraId();
  }

  private applyExternalArchiveSeek(timeMs: number) {
    this.centerTimelineViewportAfterExternalSeek(timeMs);
    this.playbackMode = 'archive';
    const boundedTimeMs = this.clampToVideoInterval(timeMs);
    this.startPendingPlayerSeek(boundedTimeMs);
    this.playbackTimeMs = boundedTimeMs;
    this.playerRemountKey += 1;
  }

  private centerTimelineViewportAfterExternalSeek(timeMs: number) {
    if (this.videoInterval != null || this.selectedCameras.length === 0) {
      return;
    }

    const liveEdgeMs = this.latestSelectedLiveEdgeMs;
    let changed = false;
    const nextPlayerSettingsByCameraId = { ...this.playerSettingsByCameraId };

    this.selectedCameras.forEach((camera) => {
      const cameraKey = this.context.getCameraSettingsKey(camera);
      const settings = nextPlayerSettingsByCameraId[cameraKey];
      const timelineViewport = getCenteredTimelineViewportAfterExternalSeek({
        liveEdgeMs,
        playbackMode: this.playbackMode,
        timeMs,
        viewport: settings?.timelineViewport ?? null,
      });

      if (timelineViewport == null) {
        return;
      }

      nextPlayerSettingsByCameraId[cameraKey] = {
        ...settings,
        timelineViewport,
      };
      changed = true;
    });

    if (!changed) {
      return;
    }

    this.context.setPlayerSettingsByCameraId(nextPlayerSettingsByCameraId);
    this.context.statePublisher.setParams({
      playerSettingsByCameraId: nextPlayerSettingsByCameraId,
    });
  }

  private clampToVideoInterval(timeMs: number) {
    if (this.videoInterval == null) {
      return timeMs;
    }

    return Math.min(Math.max(timeMs, this.videoInterval.fromMs), this.videoInterval.toMs);
  }

  private isVideoIntervalEndReport(timeMs: number, boundedTimeMs: number) {
    return this.videoInterval != null && boundedTimeMs === this.videoInterval.toMs && timeMs >= this.videoInterval.toMs;
  }

  private savePlayerPlaybackState(
    camera: TCamera,
    {
      immediate,
      playbackMode,
      playbackTimeMs,
    }: {
      immediate: boolean;
      playbackMode: VideoBroadcastPlaybackMode;
      playbackTimeMs: number;
    },
  ) {
    const cameraKey = this.context.getCameraSettingsKey(camera);

    if (!immediate) {
      this.scheduleLocalPlaybackStateSave(cameraKey, {
        playbackMode,
        playbackTimeMs,
      });
      return;
    }

    this.clearScheduledLocalPlaybackStateSave(cameraKey);
    this.setSavedPlaybackStateByCameraKey(
      cameraKey,
      {
        playbackMode,
        playbackStateSource: 'local',
        playbackTimeMs,
      },
      {
        immediate,
      },
    );
  }

  private scheduleLocalPlaybackStateSave(cameraKey: string, playbackState: PendingLocalPlaybackStateSave) {
    this.pendingLocalPlaybackStateSaves.set(cameraKey, playbackState);

    if (this.localPlaybackStateSaveTimers.has(cameraKey)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      this.localPlaybackStateSaveTimers.delete(cameraKey);
      const pendingPlaybackState = this.pendingLocalPlaybackStateSaves.get(cameraKey);

      if (!pendingPlaybackState) {
        return;
      }

      this.pendingLocalPlaybackStateSaves.delete(cameraKey);
      this.setSavedPlaybackStateByCameraKey(
        cameraKey,
        {
          playbackMode: pendingPlaybackState.playbackMode,
          playbackStateSource: 'local',
          playbackTimeMs: pendingPlaybackState.playbackTimeMs,
        },
        {
          immediate: false,
        },
      );
    }, LOCAL_PLAYBACK_STATE_SAVE_INTERVAL_MS);

    this.localPlaybackStateSaveTimers.set(cameraKey, timeoutId);
  }

  private clearScheduledLocalPlaybackStateSave(cameraKey: string) {
    const timeoutId = this.localPlaybackStateSaveTimers.get(cameraKey);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    this.localPlaybackStateSaveTimers.delete(cameraKey);
    this.pendingLocalPlaybackStateSaves.delete(cameraKey);
  }

  private setSavedPlaybackStateByCameraKey(
    cameraKey: string,
    playbackState: FlussonicHlsVideoPlayerSavedPlaybackState,
    options: PlayerSettingsUpdateOptions,
  ) {
    this.updatePlayerSettingsByCameraKey(cameraKey, playbackState, options);
  }

  private get liveModeBlocked() {
    return this.selectedCameras.length > 0 && this.selectedCameras.every((camera) => camera.liveAvailable === false);
  }

  private get latestSelectedLiveEdgeMs() {
    return this.selectedCameras.reduce<number | null>((latestTimeMs, camera) => {
      if (camera.liveAvailable === false || camera.liveEdgeMs == null) {
        return latestTimeMs;
      }

      return latestTimeMs == null ? camera.liveEdgeMs : Math.max(latestTimeMs, camera.liveEdgeMs);
    }, null);
  }

  private startPendingPlayerSeek(timeMs: number) {
    this.pendingPlayerSeek = {
      missingArchiveCameraKeys: [],
      previousTimeMs: this.playbackTimeMs,
      timeMs,
    };
  }

  private clearPendingPlayerSeek() {
    this.pendingPlayerSeek = null;
  }

  private shouldAcceptPlayerTimeAfterSeek(timeMs: number) {
    if (this.pendingPlayerSeek == null) {
      return true;
    }

    const confirmedSeekPlayback = hasConfirmedPendingSeekPlayback({
      reportedTimeMs: timeMs,
      targetTimeMs: this.pendingPlayerSeek.timeMs,
    });

    if (confirmedSeekPlayback) {
      this.clearPendingPlayerSeek();
      return true;
    }

    return false;
  }

  private shouldAcceptPlayerProgressTime(timeMs: number) {
    return isPlayerProgressTimeAccepted({
      currentTimeMs: this.playbackTimeMs,
      reportedTimeMs: timeMs,
    });
  }

  private setSyncedPlayerPlaybackRate(
    sourceCameraKey: string,
    settings: Partial<FlussonicHlsVideoPlayerViewSettings>,
    options: PlayerSettingsUpdateOptions = {},
  ) {
    const nextPlayerSettingsByCameraId = { ...this.playerSettingsByCameraId };
    const playbackRate = settings.playbackRate;
    const syncedCameraKeys = new Set([
      sourceCameraKey,
      ...this.selectedCameras.map((camera) => this.context.getCameraSettingsKey(camera)),
    ]);

    syncedCameraKeys.forEach((cameraKey) => {
      nextPlayerSettingsByCameraId[cameraKey] = {
        ...nextPlayerSettingsByCameraId[cameraKey],
        ...(cameraKey === sourceCameraKey ? settings : { playbackRate }),
      };
    });

    this.context.setPlayerSettingsByCameraId(nextPlayerSettingsByCameraId);
    this.publishPlayerSettings(nextPlayerSettingsByCameraId, options);
  }

  private setPlayerViewSettingsByCameraKey(
    cameraKey: string,
    settings: Partial<FlussonicHlsVideoPlayerViewSettings>,
    options: PlayerSettingsUpdateOptions = {},
  ) {
    this.updatePlayerSettingsByCameraKey(cameraKey, settings, options);
  }

  private updatePlayerSettingsByCameraKey(
    cameraKey: string,
    settings: Partial<FlussonicHlsVideoPlayerSettings>,
    options: PlayerSettingsUpdateOptions = {},
  ) {
    const nextSettings = {
      ...this.playerSettingsByCameraId[cameraKey],
      ...settings,
    };

    const nextPlayerSettingsByCameraId = {
      ...this.playerSettingsByCameraId,
      [cameraKey]: nextSettings,
    };

    this.context.setPlayerSettingsByCameraId(nextPlayerSettingsByCameraId);
    this.publishPlayerSettings(nextPlayerSettingsByCameraId, options);
  }

  private publishPlayerSettings(
    playerSettingsByCameraId: VideoBroadcastStateParams['playerSettingsByCameraId'],
    options: PlayerSettingsUpdateOptions,
  ) {
    const params = {
      playerSettingsByCameraId,
    };

    if (options.immediate) {
      this.context.statePublisher.setParams(params);
      return;
    }

    this.context.statePublisher.debouncedSetParams(params);
  }
}

function getLocalSavedPlaybackState(
  settings: FlussonicHlsVideoPlayerSettings | undefined,
): FlussonicHlsVideoPlayerSavedPlaybackState | null {
  if (settings?.playbackStateSource !== 'local') {
    return null;
  }

  return {
    playbackMode: settings.playbackMode,
    playbackStateSource: settings.playbackStateSource,
    playbackTimeMs: settings.playbackTimeMs,
  };
}
